import { z as zod } from 'zod'
import { FIVETRAN, reFivetranId } from './fivetran/constants'
import { PLATFORM as FIVETRAN_PLATFORM } from './fivetran/platforms'
import logger from 'shared/logger'
import {
  SUPERMETRICS,
  PLATFORM as SUPERMETRICS_PLATFORM,
  type SupermetricsPlatform,
  ERROR_CODES as SUPERMETRICS_ERROR_CODES
} from './supermetrics'
import { type SupportedPlatform as FivetranPlatform } from 'shared/thirdParty/fivetran/platforms'

const log = logger().child({ namespace: 'shared/thirdParty' })

const THIRD_PARTY_PROVIDER = {
  FIVETRAN,
  SUPERMETRICS
} as const

export const ThirdPartyProvidersList = [FIVETRAN, SUPERMETRICS] as const

type ThirdPartyProviders = (typeof ThirdPartyProvidersList)[number]

const PLATFORM = {
  ...FIVETRAN_PLATFORM,
  ...SUPERMETRICS_PLATFORM
} as const

export const PlatformsList = [FIVETRAN_PLATFORM.PINTEREST_ADS, ...Object.values(SUPERMETRICS_PLATFORM)] as const

type Platforms = (typeof PlatformsList)[number]

export const ErrorCodesEnum = {
  NOT_SPECIFIC_ACCOUNTS: 'not_specific_accounts',
  NO_ACCOUNTS: 'no_accounts',
  NOT_A_SINGLE_ACCOUNT: 'not_a_single_account',
  NOT_THE_SAME_ACCOUNT: 'not_the_same_account',
  ACCOUNT_ALREADY_EXISTS: 'account_already_exists',
  ERROR_GETTING_CLIENT_ACCOUNTS: 'error_getting_client_accounts',
  THIRD_PARTY_NOT_FOUND: 'third_party_not_found',
  THIRD_PARTY_INVALID: 'third_party_invalid',
  UNKNOWN: 'default',
  SYSTEM_SETUP_INCOMPLETE: 'system_setup_incomplete'
} as const

export type ErrorCode = (typeof ErrorCodesEnum)[keyof typeof ErrorCodesEnum]

export const ErrorCodesList = Object.values(ErrorCodesEnum)

export const ErrorCodesCanRetry: Partial<Record<ErrorCode, boolean>> = {
  [ErrorCodesEnum.NOT_SPECIFIC_ACCOUNTS]: true,
  [ErrorCodesEnum.NO_ACCOUNTS]: true,
  [ErrorCodesEnum.NOT_A_SINGLE_ACCOUNT]: true,
  [ErrorCodesEnum.NOT_THE_SAME_ACCOUNT]: true,
  [ErrorCodesEnum.ACCOUNT_ALREADY_EXISTS]: true
}

export const ACCOUNT_ID_KEY = 'account_id'
export const CREDENTIAL_ID_KEY = 'credential_id'
export const ERROR_KEY = 'error'
export const THIRD_PARTY_ID_KEY = 'third_party_id'
export const RECONNECT_ACCOUNT_ID_KEY = 'reconnect_account_id'

export type ThirdPartyResponseKey =
  | typeof CREDENTIAL_ID_KEY
  | typeof ACCOUNT_ID_KEY
  | typeof ERROR_KEY
  | typeof THIRD_PARTY_ID_KEY
  | typeof RECONNECT_ACCOUNT_ID_KEY

interface PrepareThirdPartyResponseOk {
  redirect: string
  thirdPartyId: string
  credentialId: string
  error?: null
  errorCode?: null
}

interface PrepareThirdPartyResponseError {
  redirect?: null
  thirdPartyId?: string | null
  credentialId?: string | null
  error: string
  errorCode?: ErrorCode | null
  statusCode?: number
}

type PrepareThirdPartyResponse = PrepareThirdPartyResponseOk | PrepareThirdPartyResponseError

interface ConnectorError {
  message?: string
  connectorId?: string
  code: string
  traceId?: string
}

export class ThirdPartyConnectorError extends Error {
  connectorId: string | undefined
  code: string
  message: string

  constructor(error: ConnectorError) {
    super(error.message)
    this.code = error.code
    this.message = error.message ?? error.code
    this.connectorId = error.connectorId
  }
}

const prepareThirdPartyDataSource = async ({
  clientId,
  provider,
  platform,
  credentialId,
  customQueryParams
}: {
  clientId: number
  provider: ThirdPartyProviders
  platform: Platforms
  credentialId?: string | null
  customQueryParams?: Record<string, string>
}): Promise<PrepareThirdPartyResponse> => {
  log.info({ clientId, provider, platform, credentialId }, 'prepareThirdPartyDataSource')

  const queryParams = [`clientId=${encodeURIComponent(clientId)}`]
  if (credentialId != null) {
    queryParams.push(`credentialId=${encodeURIComponent(credentialId)}`)
  }

  if (customQueryParams != null) {
    queryParams.push(...Object.entries(customQueryParams).map(([key, value]) => `${key}=${encodeURIComponent(value)}`))
  }

  return await fetch(`/api/data-source/${provider}/prepare/${platform}?${queryParams.join('&')}`).then(
    async (res) => await res.json()
  )
}

const reconnectThirdPartyDataSource = async (
  clientId: number,
  provider: ThirdPartyProviders,
  platform: Platforms,
  credentialId: string,
  thirdPartyId: string,
  reconnectAccountId?: string | null
): Promise<PrepareThirdPartyResponse> => {
  log.info({ clientId, provider, platform, credentialId, reconnectAccountId }, 'reconnectThirdParty')

  const queryParams = [
    `clientId=${encodeURIComponent(clientId)}`,
    `credentialId=${encodeURIComponent(credentialId)}`,
    `thirdPartyId=${encodeURIComponent(thirdPartyId)}`
  ]
  if (reconnectAccountId != null) {
    queryParams.push(`reconnectAccountId=${encodeURIComponent(reconnectAccountId)}`)
  }

  return await fetch(`/api/data-source/${provider}/reconnect/${platform}?${queryParams.join('&')}`).then(
    async (res) => await res.json()
  )
}

const cleanupThirdParty = async (
  clientId: number,
  provider: ThirdPartyProviders,
  platform: Platforms,
  thirdPartyId: string
): Promise<void> => {
  log.info({ clientId, provider, platform, thirdPartyId }, 'cleanupThirdParty')

  await fetch(
    `/api/data-source/${provider}/cleanup/${platform}?clientId=${encodeURIComponent(
      clientId
    )}&thirdPartyId=${encodeURIComponent(thirdPartyId)}`
  )
}

const thirdPartyIdREs: Record<string, RegExp> = {
  [FIVETRAN]: reFivetranId
}

interface ThirdPartyConnector {
  provider: ThirdPartyProviders
  platform: Platforms
  connectorId: string
  schema: string
  accountId: string
  tables: string[]
}

const validateThirdPartyId = (
  provider: ThirdPartyProviders,
  thirdPartyId: string | null | undefined,
  name: string,
  ctx: zod.RefinementCtx
): void => {
  if (thirdPartyId != null && provider in thirdPartyIdREs && !thirdPartyIdREs[provider].test(thirdPartyId)) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: `Invalid ${name} for ${provider}`,
      path: [name]
    })
  }
}

export {
  cleanupThirdParty,
  PLATFORM as THIRD_PARTY_PLATFORM,
  type Platforms as ThirdPartyPlatforms,
  prepareThirdPartyDataSource,
  type PrepareThirdPartyResponse,
  reconnectThirdPartyDataSource,
  THIRD_PARTY_PROVIDER,
  type ThirdPartyConnector,
  type ThirdPartyProviders,
  validateThirdPartyId,
  type SupermetricsPlatform,
  SUPERMETRICS_PLATFORM,
  SUPERMETRICS_ERROR_CODES,
  FIVETRAN_PLATFORM,
  type FivetranPlatform
}
